import {
    type ProviderGroup,
    SECRET_NAMES,
    type SecretName,
    type SecretsPatch,
} from '@/domain/providerConfig';
import { hasClearedSecret, hasTypedSecret } from '@/routes/shared/ProviderConfig/form/formLeaves';

import type { FormDiff } from './formDiff';

export function isEmptyDiff(diff: FormDiff): boolean {
    return (
        Object.keys(diff.connection).length === 0 &&
        Object.keys(diff.secrets).length === 0 &&
        Object.keys(diff.pens).length === 0
    );
}

/** The server's requiresConnectionTest: clearing a credential needs no test. */
export function needsConnectionTest(diff: FormDiff): boolean {
    return (
        Object.keys(diff.connection).length > 0 ||
        hasTypedSecret(diff.secrets) ||
        Object.values(diff.pens).some(
            (pen) => hasTypedSecret(pen.secrets) || Object.keys(pen.connectionChanges).length > 0
        )
    );
}

export interface DiffSummary {
    pensChanged: number;
    created: number;
    removed: number;
    connectionChanged: boolean;
    /** A typed secret takes the place of one that was stored. */
    credentialsReplaced: boolean;
    /** A typed secret where none was stored, so nothing is replaced. */
    credentialsSet: boolean;
    credentialsCleared: boolean;
}

function typedNames(patch: SecretsPatch): SecretName[] {
    return SECRET_NAMES.filter((name) => typeof patch[name] === 'string');
}

/** Whether the save types a secret over a stored one, and whether it types one where none is. */
function typedSecrets(
    diff: FormDiff,
    group: ProviderGroup | null
): { replaced: boolean; set: boolean } {
    const found = { replaced: false, set: false };
    function note(stored: boolean): void {
        if (stored) found.replaced = true;
        else found.set = true;
    }
    // A site secret goes to every pen, so it replaces something when any pen holds one.
    for (const name of typedNames(diff.secrets))
        note((group?.credentials[name]?.state ?? 'notSet') !== 'notSet');
    for (const [cageId, pen] of Object.entries(diff.pens)) {
        const row = pen.create
            ? undefined
            : group?.rows.find((entry) => entry.cageId === Number(cageId));
        for (const name of typedNames(pen.secrets)) note(row?.credentials[name] === true);
    }
    return found;
}

export function summarizeDiff(diff: FormDiff, group: ProviderGroup | null): DiffSummary {
    const pens = Object.values(diff.pens);
    const typed = typedSecrets(diff, group);
    return {
        pensChanged: pens.length,
        created: pens.filter((pen) => pen.create).length,
        removed: pens.filter((pen) => pen.remove).length,
        connectionChanged:
            Object.keys(diff.connection).length > 0 ||
            pens.some((pen) => Object.keys(pen.connectionChanges).length > 0),
        credentialsReplaced: typed.replaced,
        credentialsSet: typed.set,
        credentialsCleared:
            hasClearedSecret(diff.secrets) || pens.some((pen) => hasClearedSecret(pen.secrets)),
    };
}

/** A save that only turns existing pens off, which leaves the next fetch nothing new to read. */
export function onlyDeactivatesPens(diff: FormDiff): boolean {
    const pens = Object.values(diff.pens);
    return (
        pens.length > 0 &&
        Object.keys(diff.connection).length === 0 &&
        Object.keys(diff.secrets).length === 0 &&
        pens.every(
            (pen) =>
                !pen.create &&
                !pen.remove &&
                pen.isActive === false &&
                Object.keys(pen.fields).length === 0 &&
                Object.keys(pen.secrets).length === 0
        )
    );
}
